class FluidSlider {
  constructor(root, options) {
    this.root = root;

    this.min = parseInt(root.dataset.min, 10);
    this.max = parseInt(root.dataset.max, 10);
    this.default = (root.dataset.default) ? parseInt(root.dataset.default, 10) : Math.round((this.min + this.max) / 2);
    this.step = parseInt(root.dataset.step, 10) || 1;

    this.rail = root.querySelector(".rail");
    this.fill = root.querySelector(".fill");
    this.thumb = root.querySelector(".thumb");
    this.label = root.querySelector(".thumb-label");

    this.bindTargets = (options.bindTargets || [])
      .map((sel) => {
        if (typeof sel === "string") {
          return document.querySelector(sel);
        }
        return sel;
      })
      .filter(Boolean);

    this.value = this.min;
    this.dragging = false;

    this.onChange = options.onChange || null;

    this.init();
  }

  init() {
    // ARIA setup (generic)
    this.thumb.setAttribute("role", "slider");
    this.thumb.setAttribute("tabindex", "0");
    this.thumb.setAttribute("aria-valuemin", this.min);
    this.thumb.setAttribute("aria-valuemax", this.max);
    this.thumb.setAttribute("aria-valuenow", this.value);
    this.thumb.setAttribute("aria-valuetext", String(this.value));

    this.applyConstraintsToBindings();
    this.attachTwoWayBinding();
    this.createMarkers();
    this.setValue(this.default);

    // Pointer interaction
    this.rail.addEventListener("pointerdown", (e) => {
      this.updateFromEvent(e);
    });

    this.thumb.addEventListener("pointerdown", (e) => {
      this.dragging = true;
      this.thumb.setPointerCapture(e.pointerId);
      this.thumb.focus();
      this.updateFromEvent(e);
      e.preventDefault();
    });

    window.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      this.updateFromEvent(e);
    });

    window.addEventListener("pointerup", (e) => {
      this.dragging = false;
      this.thumb.releasePointerCapture?.(e.pointerId);
    });

    // Keyboard support
    this.thumb.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "ArrowLeft":
        case "ArrowDown":
          this.setValue(this.value - this.step);
          e.preventDefault();
          break;

        case "ArrowRight":
        case "ArrowUp":
          this.setValue(this.value + this.step);
          e.preventDefault();
          break;

        case "PageUp":
          this.setValue(this.value + this.step * 10);
          e.preventDefault();
          break;

        case "PageDown":
          this.setValue(this.value - this.step * 10);
          e.preventDefault();
          break;

        case "Home":
          this.setValue(this.min);
          e.preventDefault();
          break;

        case "End":
          this.setValue(this.max);
          e.preventDefault();
          break;
      }
    });
  }

  valueToPercent(value) {
    return ((value - this.min) / (this.max - this.min)) * 100;
  }

  percentToValue(percent) {
    const raw = this.min + (percent / 100) * (this.max - this.min);

    // snap to integer step
    return Math.round(raw / this.step) * this.step;
  }

  formatValue(value) {
    return value === this.max ? `${value}+` : String(value);
  }

  setValue(value) {
    value = Math.max(this.min, Math.min(this.max, value));
    this.value = value;

    const percent = this.valueToPercent(value);

    this.fill.style.width = `${percent}%`;
    this.thumb.style.left = `${percent}%`;

    this.label.textContent = this.formatValue(value);
    this.syncTargets(value);

    // ARIA
    this.thumb.setAttribute("aria-valuenow", value);
    this.thumb.setAttribute("aria-valuetext", String(value));

    if (typeof this.onChange === "function") {
      this.onChange(value, this);
    }
  }

  syncTargets(value) {
    for (const el of this.bindTargets) {
      if (!el) continue;

      // input / textarea
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.value = value;
        el.dispatchEvent(new Event("change", { bubbles: true }));
        continue;
      }

      // everything else
      el.textContent = this.formatValue(value);
    }
  }

  attachTwoWayBinding() {
    this.bindTargets.forEach((el) => {
      if (!el || !("value" in el)) return;

      el.addEventListener("input", () => {
        const v = parseInt(el.value, 10);
        if (!isNaN(v)) this.setValue(v);
      });
    });
  }

  applyConstraintsToBindings() {
    for (const el of this.bindTargets) {
      if (!el) continue;

      // Only apply to form controls
      if (el instanceof HTMLInputElement && el.type === "number") {
        el.min = this.min;
        el.max = this.max;
        el.step = this.step;
      }
    }
  }

  updateFromEvent(e) {
    const rect = this.rail.getBoundingClientRect();
    let percent = ((e.clientX - rect.left) / rect.width) * 100;

    percent = Math.max(0, Math.min(100, percent));

    const value = this.percentToValue(percent); // ✅ correct usage
    this.setValue(value);
  }

  createMarkers() {
    const container = this.root.querySelector(".markers");
    container.innerHTML = "";

    const count = 5;
    const stepPercent = 100 / (count - 1);

    for (let i = 0; i < count; i++) {
      const percent = i * stepPercent;

      const value = Math.round(
        this.min + (percent / 100) * (this.max - this.min),
      );

      const marker = document.createElement("div");
      marker.className = "marker";
      marker.style.left = `${percent}%`;

      const line = document.createElement("div");
      line.className = "marker-line";

      const label = document.createElement("div");
      label.className = "marker-label";
      label.textContent = value;

      marker.appendChild(line);
      marker.appendChild(label);

      marker.dataset.value = value;

      container.appendChild(marker);
    }

    container.addEventListener("click", (e) => {
      const marker = e.target.closest(".marker");
      if (!marker) return;

      const value = parseInt(marker.dataset.value, 10);
      this.setValue(value);
    });
  }
}

export { FluidSlider };
